import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { useStore } from '../store/useStore';
import { api } from '../lib/api';
import {
  Mail, Settings, Shield, PenSquare, Inbox, Star, Send, FileText, Trash2,
  Clock, CalendarClock, Search, Building2, User,
} from 'lucide-react';

export default function CommandPalette() {
  const {
    paletteOpen, setPaletteOpen, openComposer, setActiveFolder, setListSearch, user,
  } = useStore();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 280);
    return () => clearTimeout(t);
  }, [q]);

  const go = (fn) => { setPaletteOpen(false); setQ(''); fn(); };

  const { data: mailHits } = useQuery({
    queryKey: ['palette-search', debounced],
    queryFn: async () => (await api.get(`/mail/search/unified?q=${encodeURIComponent(debounced)}&folder=all&limit=8`)).data,
    enabled: paletteOpen && debounced.length >= 2,
    staleTime: 15000,
  });

  const { data: dirData } = useQuery({
    queryKey: ['palette-dir', debounced],
    queryFn: async () => (await api.get(`/directory?q=${encodeURIComponent(debounced)}`)).data,
    enabled: paletteOpen && debounced.length >= 1,
    staleTime: 30000,
  });

  const messages = mailHits?.messages || [];
  const people = (dirData?.items || []).filter((p) => p.email).slice(0, 6);

  const shortcuts = useMemo(() => [
    { id: 'compose', label: 'Redactar correo', icon: PenSquare, run: () => openComposer('new') },
    { id: 'inbox', label: 'Bandeja de entrada', icon: Inbox, run: () => { nav('/mail'); setActiveFolder('inbox'); } },
    { id: 'starred', label: 'Destacados', icon: Star, run: () => { nav('/mail'); setActiveFolder('starred'); } },
    { id: 'snoozed', label: 'Pospuestos', icon: Clock, run: () => { nav('/mail'); setActiveFolder('snoozed'); } },
    { id: 'scheduled', label: 'Programados', icon: CalendarClock, run: () => { nav('/mail'); setActiveFolder('scheduled'); } },
    { id: 'sent', label: 'Enviados', icon: Send, run: () => { nav('/mail'); setActiveFolder('sent'); } },
    { id: 'drafts', label: 'Borradores', icon: FileText, run: () => { nav('/mail'); setActiveFolder('drafts'); } },
    { id: 'trash', label: 'Papelera', icon: Trash2, run: () => { nav('/mail'); setActiveFolder('trash'); } },
    { id: 'settings', label: 'Ajustes', icon: Settings, run: () => nav('/settings') },
  ], [nav, openComposer, setActiveFolder]);

  const qLower = q.trim().toLowerCase();
  const filteredShortcuts = qLower
    ? shortcuts.filter((s) => s.label.toLowerCase().includes(qLower) || s.id.includes(qLower))
    : shortcuts;

  const runMailSearch = () => {
    if (!q.trim()) return;
    go(() => {
      nav('/mail');
      setActiveFolder('inbox');
      setListSearch(q.trim());
    });
  };

  return (
    // Sin el filtro difuso de cmdk (B11): los atajos se filtran arriba y el correo y las personas ya
    // vienen filtrados del servidor; cmdk volvía a filtrar por asunto/remitente y ocultaba resultados buenos
    <CommandDialog open={paletteOpen} onOpenChange={setPaletteOpen} commandProps={{ shouldFilter: false }}>
      <CommandInput
        placeholder="Buscar correo, personas o atajos… (de: para: asunto:)"
        value={q}
        onValueChange={setQ}
        data-testid="command-input"
      />
      <CommandList>
        <CommandEmpty>
          {debounced.length >= 2 ? 'Sin resultados.' : 'Escribe para buscar o elige un atajo.'}
        </CommandEmpty>

        {q.trim().length >= 2 && (
          <CommandGroup heading="Búsqueda">
            <CommandItem onSelect={runMailSearch} data-testid="palette-run-search">
              <Search className="h-4 w-4 mr-2" />
              Buscar «{q.trim()}» en el correo
            </CommandItem>
          </CommandGroup>
        )}

        {messages.length > 0 && (
          <CommandGroup heading="Correos">
            {messages.map((m) => (
              <CommandItem
                key={`${m.accountId}-${m.folder}-${m.uid}`}
                onSelect={() => go(() => {
                  nav('/mail');
                  setActiveFolder(m.folder || 'inbox');
                  useStore.getState().openMessage(m.accountId, m.folder, m.uid);
                })}
              >
                <Mail className="h-4 w-4 mr-2 shrink-0" />
                <span className="truncate flex-1">{m.subject || '(sin asunto)'}</span>
                <span className="text-xs text-muted-foreground ml-2 shrink-0 max-w-[120px] truncate">
                  {m.from?.name || m.from?.email}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {people.length > 0 && (
          <CommandGroup heading="Directorio">
            {people.map((p) => (
              <CommandItem
                key={`${p.source}-${p.id}`}
                onSelect={() => go(() => openComposer('new', { to: [p.email] }))}
              >
                {p.source === 'org'
                  ? <Building2 className="h-4 w-4 mr-2 shrink-0" />
                  : <User className="h-4 w-4 mr-2 shrink-0" />}
                <span className="truncate flex-1">{p.name}</span>
                <span className="text-xs text-muted-foreground ml-2 shrink-0 max-w-[140px] truncate">
                  {p.email}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading="Acciones y carpetas">
          {filteredShortcuts.map((s) => {
            const Icon = s.icon;
            return (
              <CommandItem key={s.id} onSelect={() => go(s.run)}>
                <Icon className="h-4 w-4 mr-2" />
                {s.label}
              </CommandItem>
            );
          })}
          {user?.role === 'owner' || user?.role === 'admin' ? (
            <CommandItem onSelect={() => go(() => nav('/admin'))}>
              <Shield className="h-4 w-4 mr-2" />Administración
            </CommandItem>
          ) : null}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
